// Admin → Integrations hub. Renders a card grid of integration
// categories (data sources, dev tools, workspace, AI, messaging)
// driven by the `public.integration_kinds` catalogue. Clicking a card
// routes into a per-kind detail page where the admin can configure
// one or many instances (multi-slot kinds allow several, single-slot
// kinds allow only one).
//
// Data flow:
//   - `loadIntegrationCatalogue(orgId)` pulls the `integration_kinds`
//     rows (static catalogue) and `list_org_integrations(orgId)`
//     (configured instances) in one round-trip. Result stored on
//     `root._integrationHub` so detail pages can re-use without a
//     second fetch.
//   - Counts per kind derive from the returned `integrations` array.
//   - The sidebar lives in the shared AdminSidebar; the hub body is a
//     card grid.

import { loadIntegrationCatalogue } from '../../integrations/index.js'

const _loadHub = async (root) => {
  const orgId = root?.jwtClaims?.workspace_org_id
  if (!orgId) {
    // Claims may not be populated at first mount (auth session resolves
    // async on cold load). Poll until they are, then fire once.
    if (!root._integrationsHubWatcher) {
      root._integrationsHubWatcher = setInterval(() => {
        const id = root?.jwtClaims?.workspace_org_id
        if (id) {
          clearInterval(root._integrationsHubWatcher)
          root._integrationsHubWatcher = null
          _loadHub(root)
        }
      }, 300)
    }
    return
  }
  const data = await loadIntegrationCatalogue(orgId)
  root.update({ _integrationHub: data })
}

// Detail-page slug for each kind (URL path segment after /admin/integrations/).
const _kindRoute = (kind) => {
  // Everything maps 1:1 except the legacy google_workspace slug.
  if (kind === 'google_workspace') return 'google'
  if (kind === 'supabase_project') return 'supabase'
  return kind
}

// Group integration_kinds rows by category for the card grid.
const _byCategory = (kinds, integrations) => {
  const byKind = {}
  for (const i of integrations || []) {
    if (!byKind[i.kind]) byKind[i.kind] = { count: 0, hasSecret: 0 }
    byKind[i.kind].count += 1
    if (i.has_secret) byKind[i.kind].hasSecret += 1
  }
  const groups = {}
  for (const k of kinds || []) {
    if (!groups[k.category]) groups[k.category] = { category: k.category, items: [] }
    groups[k.category].items.push({
      ...k,
      count: byKind[k.kind]?.count || 0,
      configured: (byKind[k.kind]?.count || 0) > 0
    })
  }
  // Stable category order.
  const order = ['data', 'dev', 'workspace', 'ai', 'messaging']
  return order
    .filter((c) => groups[c])
    .map((c) => ({
      ...groups[c],
      label: _categoryLabel(c)
    }))
    .concat(
      Object.values(groups)
        .filter((g) => !order.includes(g.category))
        .map((g) => ({ ...g, label: _categoryLabel(g.category) }))
    )
}

const _categoryLabel = (c) =>
  ({
    data: 'Data sources',
    dev: 'Developer tools',
    workspace: 'Workspace',
    ai: 'AI providers',
    messaging: 'Real-time & messaging',
    analytics: 'Analytics',
    automation: 'Automation',
    custom: 'Custom'
  })[c] || (c ? c.charAt(0).toUpperCase() + c.slice(1) : 'Other')

export const adminIntegrations = {
  extends: 'AppPage',
  metadata: { title: 'Integrations / Admin / Symbols Workspace' },
  // Integration browsing + install lives on the canvas marketplace app.
  // Redirect there instead of rendering our own hub — the Symbols
  // marketplace is the canonical surface for integration install + catalogue.
  onInit: (el, s) => {
    if (typeof window === 'undefined') return
    if (el.call('shouldRedirectNonAdmin', s)) {
      const root = el.call('findRouteRoot', el)
      if (root) el.router('/', root, {}, { pushState: true })
      return
    }
    const root = el.call('findRouteRoot', el)
    if (root) el.router('/marketplace?category=integration', root, {}, { pushState: true })
    else window.location.replace('/marketplace?category=integration')
  },
  onCreate: (el, s) => _loadHub(s.root),

  AdminSidebar: {},

  AdminMain: {
    extends: 'AppMain',
    padding: '0',

    Header: {
      extends: 'PageHeader',
      LeftGroup: { Title: { text: 'Integrations' } }
    },

    Body: {
      overflow: 'auto',
      padding: 'B',
      flow: 'y',
      gap: 'B',

      Hint: {
        tag: 'p',
        fontSize: 'Z2',
        color: 'caption',
        margin: '0',
        text: (el, s) => {
          const orgName = (s.root?.jwtClaims?.orgs || [])
            .find((o) => o.id === s.root?.jwtClaims?.workspace_org_id)?.name
          return `Connect external services to ${orgName || 'this org'}. Credentials live in Supabase Vault; governance and the editor CMS both read through the same integration registry.`
        }
      },

      Categories: {
        flow: 'y',
        gap: 'B',
        childExtends: 'IntegrationCategoryGroup',
        childrenAs: 'state',
        children: (el, s) => {
          const hub = s.root?._integrationHub
          if (!hub?.kinds) return []
          return _byCategory(hub.kinds, hub.integrations)
        }
      }
    }
  }
}

export const IntegrationCategoryGroup = {
  flow: 'y',
  gap: 'Z1',

  CategoryHeader: {
    tag: 'h2',
    fontSize: 'B',
    fontWeight: '700',
    color: 'title',
    margin: '0',
    letterSpacing: '-0.02em',
    text: (el, s) => s.label
  },

  Cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: 'A',
    childExtends: 'IntegrationCategoryCard',
    childrenAs: 'state',
    children: (el, s) => s.items || []
  }
}

export const IntegrationCategoryCard = {
  tag: 'button',
  type: 'button',
  flow: 'y',
  gap: 'Z1',
  padding: 'A',
  round: 'Z1',
  background: 'overlay',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'line',
  cursor: 'pointer',
  textAlign: 'left',
  ':hover': { borderColor: 'title' },
  transition: 'border-color 150ms ease',

  onClick: (e, el, s) => {
    const root = el.call('findRouteRoot', el)
    if (root) el.router(`/admin/integrations/${_kindRoute(s.kind)}`, root)
  },

  TopRow: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',
    Name: {
      tag: 'span',
      fontSize: 'A',
      fontWeight: '700',
      color: 'title',
      text: (el, s) => s.label
    },
    Pill: {
      padding: 'V Z',
      round: 'Y',
      fontSize: 'Y1',
      fontWeight: '700',
      background: (el, s) => (s.configured ? 'green' : 'overlayStrong'),
      color: (el, s) => (s.configured ? 'white' : 'caption'),
      text: (el, s) => {
        if (!s.configured) return s.allows_multiple ? 'Add' : 'Not configured'
        if (s.allows_multiple) return `${s.count} connected`
        return 'Configured'
      }
    }
  },

  Description: {
    tag: 'p',
    fontSize: 'Z2',
    color: 'caption',
    margin: '0',
    lineHeight: '1.4',
    text: (el, s) => s.description || ''
  }
}
